/**
 * File: About.jsx
 * Path: /src/pages/About.jsx
 * Description: About page with modern styling components and CareerHighlight
 * Key Features: Using ModernCtaSection for consistent styling
 */

import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import ModernCtaSection from '../components/common/ModernCtaSection';
import CareerHighlight from '../components/about/CareerHighlight';
import useScrollAnimation from '../hooks/useScrollAnimation';

const About = () => {
  // Initialize scroll animations
  useScrollAnimation();

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 -z-10 w-1/3 h-1/3 bg-primary-100 rounded-bl-full opacity-70"></div>
        <div className="absolute bottom-0 left-0 -z-10 w-1/4 h-1/4 bg-secondary-100 rounded-tr-full opacity-70"></div>
        
        <div className="container-padding mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              About <span className="gradient-text">Veritect.ai</span>
            </motion.h1>
            <motion.p 
              className="text-xl text-dark-600 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              We're on a mission to transform legal practice through 
              innovative AI solutions that empower attorneys to work 
              smarter, faster, and with greater insight.
            </motion.p>
          </div>
        </div>
      </section>
      
      {/* Company Overview Section */}
      <section className="py-20 bg-white">
        <div className="container-padding mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-on-scroll">
              <span className="text-primary-600 font-semibold">Company Overview</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
                Revolutionizing Legal Practice with AI
              </h2>
              <p className="text-dark-600 mb-4">
                Veritect.ai combines deep legal expertise with cutting-edge AI technology 
                to create solutions that address the real challenges faced by modern law firms.
              </p>
              <p className="text-dark-600 mb-4">
                Our technology takes care of the grunt work involved in the legal profession to improve efficiency, 
                accuracy, and strategic decision-making for legal firms.
              </p>
              <p className="text-dark-600 mb-8">
                Our comprehensive suite of AI-powered solutions includes advanced legal research capabilities, 
                intelligent contract analysis, and predictive analytics that are transforming how 
                legal professionals work.
              </p>
              <Button variant="primary">
                Learn More About Our Solutions
              </Button>
            </div>
            
            <div className="animate-on-scroll" data-delay="200">
              {/* Fancy AI Legal Tech Visualization */}
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <div className="aspect-video bg-gradient-to-br from-primary-700 to-secondary-700 p-6">
                  <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-0 left-0 w-full h-full">
                      {/* Abstract circuit pattern */}
                      <svg width="100%" height="100%" viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg">
                        <g fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M769.5,591.5 L417.5,591.5 L417.5,196.5 L365.5,196.5 L365.5,591.5 L57.5,591.5" stroke="rgba(255,255,255,0.3)" />
                          <path d="M627.5,358.5 L627.5,520.5 L493.5,520.5 L493.5,421.5 L549.5,421.5" stroke="rgba(255,255,255,0.4)" />
                          <path d="M595.5,308.5 L696.5,308.5 L696.5,224.5 L492.5,224.5 L492.5,87.5 L696.5,87.5" stroke="rgba(255,255,255,0.25)" />
                          <path d="M44.5,358.5 L44.5,144.5 L200.5,144.5 L200.5,242.5 L154.5,242.5" stroke="rgba(255,255,255,0.3)" />
                        </g>
                        <g fill="rgba(255,255,255,0.5)">
                          <circle cx="492.5" cy="224.5" r="4" />
                          <circle cx="365.5" cy="196.5" r="4" />
                          <circle cx="627.5" cy="358.5" r="4" />
                          <circle cx="200.5" cy="144.5" r="4" />
                          <circle cx="549.5" cy="421.5" r="4" />
                        </g>
                      </svg>
                    </div>
                  </div>
                  
                  <div className="h-full w-full flex items-center justify-center relative z-10">
                    <div className="relative w-full max-w-xl">
                      {/* Floating elements */}
                      <div className="absolute -top-12 -right-6 w-20 h-20 bg-white/10 backdrop-blur-lg rounded-lg border border-white/20 flex items-center justify-center rotate-12">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      </div>
                      
                      <div className="absolute -bottom-8 -left-4 w-16 h-16 bg-white/10 backdrop-blur-lg rounded-full border border-white/20 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white/80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                      
                      {/* Central AI Brain visualization */}
                      <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6 text-white">
                        <div className="flex items-center justify-between mb-6">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center mr-4">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                              </svg>
                            </div>
                            <h3 className="font-bold text-xl">Veritect AI Legal Assistant</h3>
                          </div>
                          <div className="flex space-x-2">
                            <div className="w-3 h-3 bg-accent-400 rounded-full animate-pulse"></div>
                            <div className="w-3 h-3 bg-primary-400 rounded-full animate-pulse" style={{ animationDelay: "0.5s" }}></div>
                            <div className="w-3 h-3 bg-secondary-400 rounded-full animate-pulse" style={{ animationDelay: "1s" }}></div>
                          </div>
                        </div>
                      
                        {/* Neural network visualization */}
                        <div className="mb-6 h-24 flex items-center justify-center">
                          <svg width="360" height="90" viewBox="0 0 360 90" xmlns="http://www.w3.org/2000/svg">
                            <g>
                              {/* Input layer */}
                              <circle cx="30" cy="20" r="6" fill="rgba(255,255,255,0.8)" />
                              <circle cx="30" cy="45" r="6" fill="rgba(255,255,255,0.8)" />
                              <circle cx="30" cy="70" r="6" fill="rgba(255,255,255,0.8)" />
                              
                              {/* Hidden layer 1 */}
                              <circle cx="120" cy="15" r="6" fill="rgba(59,130,246,0.8)" />
                              <circle cx="120" cy="40" r="6" fill="rgba(59,130,246,0.8)" />
                              <circle cx="120" cy="65" r="6" fill="rgba(59,130,246,0.8)" />
                              
                              {/* Hidden layer 2 */}
                              <circle cx="210" cy="25" r="6" fill="rgba(139,92,246,0.8)" />
                              <circle cx="210" cy="50" r="6" fill="rgba(139,92,246,0.8)" />
                              <circle cx="210" cy="75" r="6" fill="rgba(139,92,246,0.8)" />
                              
                              {/* Output */}
                              <circle cx="300" cy="30" r="6" fill="rgba(234,179,8,0.8)" />
                              <circle cx="300" cy="60" r="6" fill="rgba(234,179,8,0.8)" />
                              
                              {/* Connections */}
                              <g stroke="rgba(255,255,255,0.3)" strokeWidth="1">
                                {/* Input to hidden 1 connections */}
                                <line x1="36" y1="20" x2="114" y2="15" />
                                <line x1="36" y1="20" x2="114" y2="40" />
                                <line x1="36" y1="20" x2="114" y2="65" />
                                
                                <line x1="36" y1="45" x2="114" y2="15" />
                                <line x1="36" y1="45" x2="114" y2="40" />
                                <line x1="36" y1="45" x2="114" y2="65" />
                                
                                <line x1="36" y1="70" x2="114" y2="15" />
                                <line x1="36" y1="70" x2="114" y2="40" />
                                <line x1="36" y1="70" x2="114" y2="65" />
                                
                                {/* Hidden 1 to hidden 2 connections */}
                                <line x1="126" y1="15" x2="204" y2="25" />
                                <line x1="126" y1="15" x2="204" y2="50" />
                                <line x1="126" y1="15" x2="204" y2="75" />
                                
                                <line x1="126" y1="40" x2="204" y2="25" />
                                <line x1="126" y1="40" x2="204" y2="50" />
                                <line x1="126" y1="40" x2="204" y2="75" />
                                
                                <line x1="126" y1="65" x2="204" y2="25" />
                                <line x1="126" y1="65" x2="204" y2="50" />
                                <line x1="126" y1="65" x2="204" y2="75" />
                                
                                {/* Hidden 2 to output connections */}
                                <line x1="216" y1="25" x2="294" y2="30" />
                                <line x1="216" y1="25" x2="294" y2="60" />
                                
                                <line x1="216" y1="50" x2="294" y2="30" />
                                <line x1="216" y1="50" x2="294" y2="60" />
                                
                                <line x1="216" y1="75" x2="294" y2="30" />
                                <line x1="216" y1="75" x2="294" y2="60" />
                              </g>
                            </g>
                          </svg>
                        </div>
                        
                        {/* Results/output section */}
                        <div className="flex space-x-4">
                          <div className="flex-1 bg-white/5 rounded-lg p-3">
                            <div className="text-xs text-white/60 mb-2">Contract Analysis</div>
                            <div className="w-full h-2 bg-white/20 rounded-full mb-2">
                              <div className="w-4/5 h-2 bg-accent-500 rounded-full"></div>
                            </div>
                            <div className="text-right text-xs font-medium">92%</div>
                          </div>
                          <div className="flex-1 bg-white/5 rounded-lg p-3">
                            <div className="text-xs text-white/60 mb-2">Legal Research</div>
                            <div className="w-full h-2 bg-white/20 rounded-full mb-2">
                              <div className="w-3/4 h-2 bg-primary-500 rounded-full"></div>
                            </div>
                            <div className="text-right text-xs font-medium">88%</div>
                          </div>
                          <div className="flex-1 bg-white/5 rounded-lg p-3">
                            <div className="text-xs text-white/60 mb-2">Case Prediction</div>
                            <div className="w-full h-2 bg-white/20 rounded-full mb-2">
                              <div className="w-2/3 h-2 bg-secondary-500 rounded-full"></div>
                            </div>
                            <div className="text-right text-xs font-medium">78%</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Our Values */}
      <section className="py-20 bg-primary-50">
        <div className="container-padding mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16 animate-on-scroll">
            <span className="text-primary-600 font-semibold">Our Values</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2 mb-6">
              What Drives Us
            </h2>
            <p className="text-dark-500 text-lg">
              At Veritect.ai, our core values inform everything we do, from product 
              development to customer support.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                ),
                title: 'Innovation',
                description: "We constantly push the boundaries of what's possible with legal technology, combining the latest AI advances with deep legal domain expertise."
              },
              {
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                  </svg>
                ),
                title: 'Integrity',
                description: 'We uphold the highest ethical standards in everything we do, from how we build our AI systems to how we handle sensitive client data.'
              },
              {
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                ),
                title: 'Impact',
                description: 'We measure our success by the tangible difference our solutions make in the daily work of legal professionals and the outcomes they achieve for their clients.'
              }
            ].map((value, index) => (
              <Card key={index} className="text-center animate-on-scroll" data-delay={index * 150}>
                <div className="text-primary-500 mb-4 flex justify-center">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                <p className="text-dark-500">{value.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>
      
      {/* Career Highlight Section */}
      <CareerHighlight />
      
      {/* CTA - Using ModernCtaSection instead of custom dark section */}
      <ModernCtaSection
        title="Join Us on Our Mission"
        description="Whether you're looking to transform your legal practice with our AI solutions or join our team, we'd love to hear from you."
      >
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Button variant="primary">
            Explore Our Solutions
          </Button>
          <Button 
            variant="outline" 
            className="text-white border-white hover:bg-white/10"
            to="/contact"
          >
            Contact Us
          </Button>
        </div>
      </ModernCtaSection>
    </motion.div>
  );
};

export default About;